import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useLocation } from "react-router-dom";
import { API } from "../../config/api";

export default function AddPayment() {
  const location = useLocation();

  const [designers, setDesigners] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
  });

  const [form, setForm] = useState({
    designer_id: "",
    amount: "",
    payment_type: "Cash",
    transaction_no: "",
    payment_date: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [errors, setErrors] = useState({});

  // Auto fill from designer page
  useEffect(() => {
    if (location.state) {
      setForm((prev) => ({
        ...prev,
        designer_id: location.state.designer_id || "",
        amount: location.state.pending_amount || "",
      }));
    }
  }, [location.state]);

  useEffect(() => {
    loadDesigners();
    loadPayments(1, filters);
  }, []);

  const loadDesigners = async () => {
    try {
      const res = await fetch(API.ADMIN_DESIGNER_STATISTICS);
      const data = await res.json();
      if (data.status) {
        setDesigners(data.designers || []);
      }
    } catch (error) {
      console.error("Designer Load Error:", error);
    }
  };

  const loadPayments = async (currentPage = 1, customFilters = filters) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        action: "get_designer_payments",
        page: currentPage,
        limit: 10,
        from_date: customFilters.from_date || "",
        to_date: customFilters.to_date || "",
      });

      const res = await fetch(`${API.BASE_URL}/api.php?${query}`);
      const data = await res.json();

      if (data.status) {
        setPayments(data.payments || []);
        setPage(data.pagination?.page || 1);
        setTotalPages(data.pagination?.total_pages || 1);
      }
    } catch (error) {
      console.error("Payment Load Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const designerOptions = designers.map((item) => ({
    value: item.id,
    label: `${item.name} (₹${Number(item.pending_amount || 0).toLocaleString("en-IN")})`,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Automatic filter trigger on date change
  const handleFilterDateChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);
    loadPayments(1, updatedFilters);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.designer_id) {
      newErrors.designer_id = "Please select a designer.";
    }
    if (!form.amount || Number(form.amount) <= 0) {
      newErrors.amount = "Please enter a valid amount greater than 0.";
    }
    if (!form.payment_date) {
      newErrors.payment_date = "Please select a date.";
    }
    if (form.payment_type !== "Cash" && !form.transaction_no.trim()) {
      newErrors.transaction_no =
        "Transaction number is recommended for non-cash modes.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const savePayment = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const api = form.id
        ? `${API.BASE_URL}/api.php?action=update_designer_payment`
        : `${API.BASE_URL}/api.php?action=add_designer_payment`;

      const res = await fetch(api, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      alert(data.message);

      if (data.status) {
        loadDesigners();
        loadPayments(page);
        setForm({
          id: "",
          designer_id: "",
          amount: "",
          payment_type: "Cash",
          transaction_no: "",
          payment_date: new Date().toISOString().split("T")[0],
          notes: "",
        });
        setErrors({});
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Smart Pagination Range Generator
  const getPaginationRange = () => {
    const total = totalPages;
    const current = page;
    const delta = 1; // Number of pages on either side of current page
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, current - delta);
      i <= Math.min(total - 1, current + delta);
      i++
    ) {
      range.push(i);
    }

    if (current - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < total - 1) {
      rangeWithDots.push("...", total);
    } else if (total > 1) {
      rangeWithDots.push(total);
    }

    return rangeWithDots;
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "42px",
      borderColor: errors.designer_id
        ? "#dc3545"
        : state.isFocused
          ? "#0f766e"
          : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 1px #0f766e" : "none",
      borderRadius: "8px",
      fontSize: "14px",
      "&:hover": {
        borderColor: errors.designer_id ? "#dc3545" : "#0f766e",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0f766e"
        : state.isFocused
          ? "#f1f5f9"
          : "#fff",
      color: state.isSelected ? "#fff" : "#334155",
      fontSize: "14px",
      cursor: "pointer",
    }),
  };
  const editPayment = (item) => {
    setForm({
      id: item.id,
      designer_id: item.designer_id,
      amount: item.amount,
      payment_type: item.payment_type,
      transaction_no: item.transaction_no || "",
      payment_date: item.payment_date,
      notes: item.notes || "",
    });
  };
  const deletePayment = async (id) => {
    if (!window.confirm("Delete this payment?")) return;

    try {
      const res = await fetch(
        `${API.BASE_URL}/api.php?action=delete_designer_payment&id=${id}`,
      );

      const data = await res.json();

      alert(data.message);

      if (data.status) {
        loadPayments(page);
        loadDesigners();
      }
    } catch (err) {
      console.log(err);
    }
  };
  return (
    <div className="w-100 px-4 py-3">
      {/* ================= PAYMENT FORM ================= */}
      <div
        className="card border-0 shadow-sm mb-4"
        style={{ borderRadius: "12px" }}
      >
        <div className="card-header bg-white border-bottom py-3 px-4">
          <h5 className="mb-0 fw-bold" style={{ color: "#0f766e" }}>
            Add Payment
          </h5>
        </div>

        <div className="card-body p-0">
          <form onSubmit={savePayment} noValidate>
            <div className="row g-3">
              {/* Designer */}
              <div className="col-lg-2 col-md-4">
                <label className="form-label fw-medium text-secondary small">
                  Payment Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${errors.payment_date ? "is-invalid" : ""}`}
                  style={{ minHeight: "42px", borderRadius: "8px" }}
                  name="payment_date"
                  value={form.payment_date}
                  onChange={handleChange}
                />
                {errors.payment_date && (
                  <div className="invalid-feedback">{errors.payment_date}</div>
                )}
              </div>

              <div className="col-lg-3 col-md-6">
                <label className="form-label fw-medium text-secondary small">
                  Designer <span className="text-danger">*</span>
                </label>
                <Select
                  options={designerOptions}
                  placeholder="Search designer..."
                  styles={customSelectStyles}
                  value={
                    designerOptions.find(
                      (opt) => opt.value === form.designer_id,
                    ) || null
                  }
                  onChange={(selected) => {
                    setForm({
                      ...form,
                      designer_id: selected ? selected.value : "",
                    });
                    if (errors.designer_id)
                      setErrors((prev) => ({ ...prev, designer_id: null }));
                  }}
                  isSearchable
                  isClearable
                />
                {errors.designer_id && (
                  <div className="text-danger extra-small mt-1">
                    {errors.designer_id}
                  </div>
                )}
              </div>

              {/* Amount */}
              <div className="col-lg-2 col-md-6">
                <label className="form-label fw-medium text-secondary small">
                  Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  step="any"
                  className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                  style={{ minHeight: "42px", borderRadius: "8px" }}
                  name="amount"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={handleChange}
                />
                {errors.amount && (
                  <div className="invalid-feedback">{errors.amount}</div>
                )}
              </div>

              {/* Method */}
              <div className="col-lg-2 col-md-4">
                <label className="form-label fw-medium text-secondary small">
                  Method <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  style={{ minHeight: "42px", borderRadius: "8px" }}
                  name="payment_type"
                  value={form.payment_type}
                  onChange={handleChange}
                >
                  <option value="Cash">Cash</option>
                  <option value="Online">Online</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>

              {/* Transaction No */}
              <div className="col-lg-3 col-md-4">
                <label className="form-label fw-medium text-secondary small">
                  Transaction No <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.transaction_no ? "is-invalid" : ""}`}
                  style={{ minHeight: "42px", borderRadius: "8px" }}
                  name="transaction_no"
                  value={form.transaction_no}
                  onChange={handleChange}
                  required
                />
                {errors.transaction_no && (
                  <div className="invalid-feedback">
                    {errors.transaction_no}
                  </div>
                )}
              </div>

              {/* Date */}

              {/* Notes */}
              <div className="col-lg-8 col-md-8">
                <label className="form-label fw-medium text-secondary small">
                  Notes / Remarks
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={{ minHeight: "42px", borderRadius: "8px" }}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Add optional notes..."
                />
              </div>

              {/* Submit Button */}
              {/* Submit Button */}
              <div className="col-lg-2 col-md-4 d-flex align-items-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn w-100 fw-semibold text-white d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: "#0f766e",
                    height: "42px",
                    borderRadius: "8px",
                  }}
                >
                  {isSubmitting
                    ? "Saving..."
                    : form.id
                      ? "Update"
                      : "Save Payment"}
                </button>
              </div>

              {/* Cancel Button (Only While Editing) */}
              {form.id && (
                <div className="col-lg-2 col-md-4 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-secondary w-100"
                    style={{
                      height: "42px",
                      borderRadius: "8px",
                    }}
                    onClick={() => {
                      setForm({
                        id: "",
                        designer_id: "",
                        amount: "",
                        payment_type: "Cash",
                        transaction_no: "",
                        payment_date: new Date().toISOString().split("T")[0],
                        notes: "",
                      });
                      setErrors({});
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* ================= FILTER & TABLE SECTION ================= */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <div className="card-header bg-white py-3 px-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <h5 className="mb-0 fw-bold text-dark">Payment History Logs</h5>

          {/* Auto Filters */}
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="d-flex align-items-center gap-1">
              <span className="small text-muted">From:</span>
              <input
                type="date"
                name="from_date"
                className="form-control form-control-sm"
                style={{ borderRadius: "6px" }}
                value={filters.from_date}
                onChange={handleFilterDateChange}
              />
            </div>

            <div className="d-flex align-items-center gap-1">
              <span className="small text-muted">To:</span>
              <input
                type="date"
                name="to_date"
                className="form-control form-control-sm"
                style={{ borderRadius: "6px" }}
                value={filters.to_date}
                onChange={handleFilterDateChange}
              />
            </div>

            {(filters.from_date || filters.to_date) && (
              <button
                className="btn btn-sm btn-outline-secondary px-3"
                style={{ borderRadius: "6px" }}
                onClick={() => {
                  const reset = { from_date: "", to_date: "" };
                  setFilters(reset);
                  loadPayments(1, reset);
                }}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Full-width responsive table container */}
        <div className="table-responsive w-100">
          <table
            className="table table-hover align-middle mb-0 w-100"
            style={{ borderCollapse: "separate", borderSpacing: 0 }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th
                  className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold"
                  style={{ width: "80px" }}
                >
                  #
                </th>
                <th className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold">
                  Date
                </th>
                <th className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold">
                  Designer
                </th>
                <th className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold">
                  Amount
                </th>
                <th className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold">
                  Method
                </th>
                <th className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold">
                  Tran No.
                </th>
                <th className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold">
                  Notes
                </th>
                <th className="py-3 px-4 text-secondary text-uppercase fs-7 fw-bold">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      style={{ color: "#0f766e" }}
                    ></div>
                    Loading payments...
                  </td>
                </tr>
              ) : payments.length > 0 ? (
                payments.map((item, index) => (
                  <tr
                    key={item.id || index}
                    style={{ borderBottom: "1px solid #f1f5f9" }}
                  >
                    <td className="py-3 px-4 text-muted small fw-medium">
                      {payments.length - index + (page - 1) * 10}
                    </td>

                    <td className="py-3 px-4 text-dark small fw-medium">
                      {new Date(item.payment_date).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>

                    <td className="py-3 px-4 fw-semibold text-dark">
                      {item.designer_name}
                    </td>

                    <td
                      className="py-3 px-4 fw-bold"
                      style={{ color: "#0f766e" }}
                    >
                      ₹{Number(item.amount).toLocaleString("en-IN")}
                    </td>

                    <td className="py-3 px-4">
                      <span
                        className={`badge rounded-pill px-2.5 py-1.5 fw-medium ${
                          item.payment_type === "Cash"
                            ? "bg-success-subtle text-success border border-success-subtle"
                            : item.payment_type === "UPI"
                              ? "bg-info-subtle text-info border border-info-subtle"
                              : "bg-primary-subtle text-primary border border-primary-subtle"
                        }`}
                      >
                        {item.payment_type}
                      </span>
                    </td>

                    <td className="py-3 px-4 text-secondary small">
                      {item.transaction_no ? (
                        <code className="text-dark bg-light px-2 py-1 rounded">
                          {item.transaction_no}
                        </code>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    <td
                      className="py-3 px-4 text-secondary small"
                      style={{ maxWidth: "250px" }}
                    >
                      <span
                        className="text-truncate d-block"
                        title={item.notes}
                      >
                        {item.notes || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => editPayment(item)}
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => deletePayment(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    No transaction records found matching the criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ================= NUMBERED PAGINATION FOOTER ================= */}
        <div className="card-footer bg-white border-top py-3 px-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-1">
            <button
              className="btn btn-outline-secondary btn-sm px-2 py-1"
              style={{ borderRadius: "6px" }}
              disabled={page === 1 || loading}
              onClick={() => loadPayments(page - 1)}
            >
              Previous
            </button>

            {getPaginationRange().map((item, idx) =>
              item === "..." ? (
                <span key={`dots-${idx}`} className="px-2 text-muted small">
                  ...
                </span>
              ) : (
                <button
                  key={`page-${item}`}
                  className={`btn btn-sm ${
                    page === item ? "text-white" : "btn-outline-secondary"
                  }`}
                  style={{
                    borderRadius: "6px",
                    backgroundColor: page === item ? "#0f766e" : "transparent",
                    borderColor: page === item ? "#0f766e" : "#cbd5e1",
                    minWidth: "32px",
                  }}
                  disabled={loading}
                  onClick={() => loadPayments(item)}
                >
                  {item}
                </button>
              ),
            )}

            <button
              className="btn btn-outline-secondary btn-sm px-2 py-1"
              style={{ borderRadius: "6px" }}
              disabled={page === totalPages || loading}
              onClick={() => loadPayments(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
